import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, DollarSign, Package, Search, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getContract, getContractPricing, createContractPrice, updateContractPrice, deleteContractPrice, deleteAllContractPricing, getProducts } from '../../config/api';

const ContractPricing = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productSearchRef = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterForm, setFilterForm] = useState('ALL');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  
  // Product search state
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    productId: '',
    price: '',
    effectiveFrom: '',
    effectiveTo: '',
    notes: ''
  });

  // Fetch contract details
  const { data: contract, isLoading: contractLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => getContract(id),
    enabled: !!id
  });

  // Fetch contract pricing
  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ['contract-pricing', id],
    queryFn: () => getContractPricing(id),
    enabled: !!id
  });

  const prices = pricingData?.prices || [];

  // Fetch all products for selection
  const { data: allProductsData, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: getProducts,
    enabled: true
  });

  const allProducts = allProductsData?.products || [];

  // Filter products based on search term
  useEffect(() => {
    if (allProducts && productSearchTerm) {
      const filtered = allProducts.filter(product => 
        product.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        product.ndc?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearchTerm.toLowerCase())
      );
      setFilteredProducts(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setFilteredProducts([]);
    }
  }, [allProducts, productSearchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
        setShowProductDropdown(false);
        setFilteredProducts([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debug logging
  console.log('ContractPricing Debug:', {
    contract,
    pricingData,
    allProductsData,
    productsError,
    allProducts: allProducts?.length || 0
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => {
      console.log('Submitting contract price data:', data);
      return createContractPrice(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-pricing', id] });
      toast.success('Price added successfully');
      setShowAddForm(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Contract price creation error:', error);
      toast.error(error.response?.data?.error || 'Failed to add price');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ priceId, data }) => updateContractPrice(id, priceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-pricing', id] });
      toast.success('Price updated successfully');
      setEditingPrice(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update price');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (priceId) => deleteContractPrice(id, priceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-pricing', id] });
      toast.success('Price deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete price');
    }
  });

  const deleteAllPricingMutation = useMutation({
    mutationFn: () => deleteAllContractPricing(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contract-pricing', id] });
      toast.success(data.message || 'All pricing deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete all pricing');
    }
  });

  const resetForm = () => {
    setFormData({
      productId: '',
      price: '',
      effectiveFrom: '',
      effectiveTo: '',
      notes: ''
    });
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setFilteredProducts([]);
  };

  const handleProductSelect = (product) => {
    setFormData(prev => ({ ...prev, productId: product.id }));
    setProductSearchTerm(product.name);
    setShowProductDropdown(false);
    setFilteredProducts([]);
  };

  const handleProductSearchChange = (e) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    setFormData(prev => ({ ...prev, productId: '' }));
    
    if (value.length > 0) {
      setShowProductDropdown(true);
    } else {
      setShowProductDropdown(false);
      setFilteredProducts([]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.productId || !formData.price || !formData.effectiveFrom) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Format the data for submission
    const submitData = {
      ...formData,
      price: parseFloat(formData.price || 0),
      effectiveFrom: new Date(formData.effectiveFrom).toISOString(),
      effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo).toISOString() : null
    };
    
    console.log('Submitting form data:', submitData);
    
    if (editingPrice) {
      updateMutation.mutate({ priceId: editingPrice.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      productId: price.productId,
      price: (price.price?.toString() || '0'),
      effectiveFrom: price.effectiveFrom?.split('T')[0] || '',
      effectiveTo: price.effectiveTo?.split('T')[0] || '',
      notes: price.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = (priceId) => {
    if (window.confirm('Are you sure you want to delete this price?')) {
      deleteMutation.mutate(priceId);
    }
  };

  const deleteAllPricing = () => {
    deleteAllPricingMutation.mutate();
  };

  const filteredPrices = prices.filter(price => {
    const matchesSearch = !searchTerm || 
      price.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      price.product?.ndc11?.includes(searchTerm);
    
    const matchesForm = filterForm === 'ALL' || price.product?.dosageForm === filterForm;
    
    return matchesSearch && matchesForm;
  });

  if (contractLoading || pricingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contract not found</h2>
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-primary-600 hover:text-primary-700"
            title="Back to Contracts"
          >
            Back to Contracts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/app/contracts')}
            className="text-gray-400 hover:text-gray-600"
            title="Back to Contracts"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Contract Pricing</h1>
            <p className="text-sm text-gray-500">
              {contract.number} - {contract.description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {prices.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete ALL pricing for this contract? This will remove ${prices.length} price entries and cannot be undone.`)) {
                  deleteAllPricing();
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
              title="Delete all pricing for this contract"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All Pricing
            </button>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            title="Add Price"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Price
          </button>
        </div>
      </div>

      {/* Contract Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contract Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Type</p>
            <p className="font-medium">{contract.type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="font-medium">{contract.status}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Effective Period</p>
            <p className="font-medium">
              {new Date(contract.effectiveFrom).toLocaleDateString()} - {new Date(contract.effectiveTo).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <select
            value={filterForm}
            onChange={(e) => setFilterForm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Forms</option>
            <option value="TABLET">Tablet</option>
            <option value="CAPSULE">Capsule</option>
            <option value="LIQUID">Liquid</option>
            <option value="INJECTION">Injection</option>
            <option value="TOPICAL">Topical</option>
            <option value="INHALATION">Inhalation</option>
            <option value="TRANSDERMAL">Transdermal</option>
            <option value="OPHTHALMIC">Ophthalmic</option>
            <option value="OTIC">Otic</option>
            <option value="NASAL">Nasal</option>
            <option value="RECTAL">Rectal</option>
            <option value="VAGINAL">Vaginal</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingPrice ? 'Edit Price' : 'Add New Price'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative" ref={productSearchRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product
                </label>
                {editingPrice ? (
                  <input
                    type="text"
                    value={editingPrice.product?.name || 'Unknown Product'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      value={productSearchTerm}
                      onChange={handleProductSearchChange}
                      placeholder="Search for a product by name, NDC, or SKU..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                    {showProductDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleProductSelect(product)}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-600">
                              {product.ndc11 && `NDC: ${product.ndc11}`}
                              {product.sku && ` • SKU: ${product.sku}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {showProductDropdown && filteredProducts.length === 0 && productSearchTerm && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                        <div className="px-4 py-2 text-gray-500">No products found</div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From
                </label>
                <input
                  type="date"
                  value={formData.effectiveFrom}
                  onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective To
                </label>
                <input
                  type="date"
                  value={formData.effectiveTo}
                  onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingPrice(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                title="Cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                title={editingPrice ? 'Update Price' : 'Add Price'}
              >
                {editingPrice ? 'Update Price' : 'Add Price'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pricing Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pricing ({filteredPrices.length})</h3>
        </div>
        
        {filteredPrices.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pricing found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterForm !== 'ALL' 
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your first price.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Effective Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPrices.map((price) => (
                  <tr key={price.id}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {price.product?.name || 'Unknown Product'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {price.product?.ndc11 || 'No NDC'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        ${Number(price.price || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(price.effectiveFrom).toLocaleDateString()}
                      </div>
                      {price.effectiveTo && (
                        <div className="text-sm text-gray-500">
                          to {new Date(price.effectiveTo).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {price.notes || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(price)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(price.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractPricing;
